import type { Meta } from '@scm/contratos';

/**
 * Cliente de la API.
 *
 * Toda respuesta viene envuelta en { datos, meta } y todo error en
 * { error: { codigo, mensaje, detalles, traza_id } }. Este modulo es el unico
 * lugar que conoce esa forma: las pantallas reciben datos o una excepcion con
 * el codigo del catalogo.
 */

export class ErrorApi extends Error {
  readonly codigo: string;
  readonly http: number;
  readonly detalles: Record<string, unknown> | undefined;
  readonly trazaId: string | undefined;

  constructor(
    codigo: string,
    mensaje: string,
    http: number,
    detalles?: Record<string, unknown>,
    trazaId?: string,
  ) {
    super(mensaje);
    this.name = 'ErrorApi';
    this.codigo = codigo;
    this.http = http;
    this.detalles = detalles;
    this.trazaId = trazaId;
  }

  /** Lo que falta para completar una accion, cuando el error lo trae. */
  get faltan(): { campo: string; motivo: string }[] {
    const f = this.detalles?.['faltan'];
    return Array.isArray(f) ? (f as { campo: string; motivo: string }[]) : [];
  }
}

interface Envoltura<T> {
  datos: T;
  meta: Meta;
}

interface CuerpoError {
  error: { codigo: string; mensaje: string; detalles?: Record<string, unknown>; traza_id: string };
}

/* La sesion viaja en cookie httpOnly, asi que no hay token que guardar aqui:
   `credentials: 'same-origin'` es todo lo que hace falta y el script de la
   pagina nunca puede leerla. */
async function pedir<T>(ruta: string, opciones: RequestInit = {}): Promise<Envoltura<T>> {
  const respuesta = await fetch(`/api${ruta}`, {
    ...opciones,
    credentials: 'same-origin',
    headers: {
      ...(opciones.body ? { 'content-type': 'application/json' } : {}),
      ...opciones.headers,
    },
  });

  /* El CSV y las descargas no vienen envueltos. */
  const tipo = respuesta.headers.get('content-type') ?? '';
  if (!tipo.includes('application/json')) {
    if (!respuesta.ok) {
      throw new ErrorApi('ETK-INT-001', 'Respuesta inesperada del servidor', respuesta.status);
    }
    return { datos: (await respuesta.text()) as unknown as T, meta: { traza_id: '' } };
  }

  const cuerpo = (await respuesta.json()) as unknown;

  if (!respuesta.ok) {
    const e = (cuerpo as CuerpoError).error;
    throw new ErrorApi(
      e?.codigo ?? 'ETK-INT-001',
      e?.mensaje ?? 'Falla inesperada',
      respuesta.status,
      e?.detalles,
      e?.traza_id,
    );
  }

  return cuerpo as Envoltura<T>;
}

export const api = {
  obtener: <T>(ruta: string) => pedir<T>(ruta),
  /* La clave `body` se OMITE cuando no hay cuerpo, en vez de ponerse en
     undefined: con exactOptionalPropertyTypes no es lo mismo, y un POST sin
     cuerpo es legitimo (despachar una guia, cerrar sesion). */
  enviar: <T>(ruta: string, datos?: unknown) =>
    pedir<T>(ruta, {
      method: 'POST',
      ...(datos === undefined ? {} : { body: JSON.stringify(datos) }),
    }),
  ajustar: <T>(ruta: string, datos: unknown) =>
    pedir<T>(ruta, { method: 'PATCH', body: JSON.stringify(datos) }),
  quitar: <T>(ruta: string) => pedir<T>(ruta, { method: 'DELETE' }),
  /* PUT: para recursos cuya llave la pone quien llama y no el servidor, como
     la tarifa de un tipo de servicio dentro de un proyecto. Con POST habria
     que inventar un identificador o adivinar si ya existe. */
  poner: <T>(ruta: string, datos: unknown) =>
    pedir<T>(ruta, { method: 'PUT', body: JSON.stringify(datos) }),
};
